/**
 * English UI strings — the source of truth. Every other locale falls back to
 * these keys (see I18nProvider.t), so the app is always fully usable in English
 * even where a translation is missing.
 *
 * Keys are dot-namespaced by area. When you wrap a new string, add its key here.
 */
export const en = {
  // Shared / Generic
  'common.missingText': 'Sorry, we are working to provide the texts.',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.skip': 'Skip',
  'common.weeksShort': 'wks',
  'common.weeksLong': 'weeks',
  'common.weeksOld': 'weeks old',
  'common.monthsOld': 'months old',
  'common.monthShort': 'mo',
  'common.monthLong': 'month',
  'common.monthsLong': 'months',
  'common.adding': 'Adding…',
  'common.saving': 'Saving…',
  'common.seeAll': 'See All',
  'common.rateLimited': 'Too many requests. Please slow down and try again shortly.',
  'common.dismiss': 'Dismiss',
  'common.done': 'Done!',
  'common.showAll': 'Show all',
  'common.hide': 'Hide',
  'common.show': 'Show',
  'common.on': 'On',
  'common.off': 'Off',

  // Authentication & Fallbacks
  'auth.welcomeBack': 'Welcome back',
  'auth.noBabyTitle': 'No baby on file yet.',
  'auth.noBabyDesc': 'Add your first baby to start tracking milestones and growth.',
  'auth.signedInAs': 'Signed in as {email}',
  'auth.addYourBaby': 'Add your baby',
  'auth.errorTitle': "Couldn't load your account",
  'auth.errorDesc': 'Sign out and back in to retry — if the problem persists, the API may be down.',
  'auth.signOut': 'Sign out',
  'auth.verification.title': 'Security Verification',
  'auth.verification.headline': 'Please verify your email',
  'auth.verification.description': 'For security reasons, you must verify your email address to continue using BabyNook. We sent a verification link to:',
  'auth.verification.checkCTA': "I've verified my email",
  'auth.verification.resendCTA': 'Resend verification email',
  'auth.verification.cooldown': 'Resend link in {seconds}s',
  'auth.verification.sentSuccess': 'Verification email sent successfully! Please check your inbox.',
  'auth.verification.resendError': 'Could not resend verification email. Please try again later.',
  'auth.verification.notVerifiedYet': "Email verification link hasn't been clicked yet. Please click the link in your email and try again.",
  'auth.verification.checkError': 'Failed to check verification status. Please try again.',
  'auth.verification.alreadyVerified': 'Your email is now verified!',
  'auth.verification.mockSent': 'Mock verification email sent!',

  // Language Selection
  'language.title': 'Language',
  'language.subtitle': 'Content & app language',
  'language.aria': 'Select language',

  // Bottom & Side Navigation
  'nav.today': 'Today',
  'nav.activities': 'Activities',
  'nav.milestones': 'Milestones',
  'nav.journey': 'Journey',
  'nav.timeline': 'Timeline',
  'nav.leaps': 'Leaps',
  'nav.growth': 'Growth',
  'nav.ai': 'Ask Alex',
  'nav.viewProfile': 'View profile',

  // AI Chatbot ("Alex")
  'ai.title': 'Ask Alex',
  'ai.newChat': 'New chat',
  'ai.history': 'Chat history',
  'ai.left': 'left',
  'ai.questions': 'questions',
  'ai.noChats': 'No chats yet.',
  'ai.placeholder': 'Ask Alex about your baby…',
  'ai.send': 'Send',
  'ai.attach': 'Attach a file',
  'ai.takePhoto': 'Take a photo',
  'ai.thinking': 'Alex is checking with two AIs…',
  'ai.verifiedByTwo': 'verified by 2 AIs',
  'ai.seeBoth': 'See both answers',
  'ai.hideBoth': 'Hide both answers',
  'ai.answerA': 'Answer A',
  'ai.answerB': 'Answer B',
  'ai.whyVote': 'Why this rating',
  'ai.sources': 'Sources',
  'ai.sourcesVia': 'via Google',
  'ai.conf.high': 'High confidence',
  'ai.conf.medium': 'Medium confidence',
  'ai.conf.low': 'Low confidence',
  'ai.conf.wrong': 'Likely incorrect',
  'ai.conf.na': 'Off-topic',
  'ai.empty.title': 'Hi, I’m Alex',
  'ai.empty.body':
    'Ask me anything about {name} — sleep, feeding, milestones, play. Every answer is double-checked by two AIs and grounded in your baby’s own data, so it’s far more reliable than a standard chatbot.',
  'ai.empty.tryAsking': 'Try asking',
  'ai.sessionFull': 'This chat reached {n} questions. Start a new chat to keep going.',
  'ai.quotaReached': 'You’ve used all {n} questions on your plan. Next question {when}.',
  'ai.upgradeToAsk': 'Upgrade your plan to ask Alex questions.',
  'ai.upgrade': 'Upgrade for more questions',
  'ai.resetSoon': 'shortly',
  'ai.inHours': 'in {n}h',
  'ai.inDays': 'in {n}d',
  'ai.justNow': 'just now',
  'ai.minsAgo': '{n}m ago',
  'ai.hrsAgo': '{n}h ago',
  'ai.daysAgo': '{n}d ago',
  'ai.disclaimer':
    'This is an AI and can make mistakes, even when providing information about people. Always use your own judgement and consult a professional for medical concerns.',

  // Profile Management
  'profile.title': 'Profile',
  'profile.babies': 'Your babies',
  'profile.switchBaby': 'Switch',
  'profile.activeBaby': 'Active',
  'profile.addAnotherBaby': 'Add another baby',
  'profile.theme': 'Theme accent',
  'profile.realName': 'Name',
  'profile.realNamePlaceholder': 'Your name (optional)',
  'profile.relation': 'Relation / Role',
  'profile.relationPlaceholder': 'Select relation (optional)',
  'profile.relation.mother': 'Mother',
  'profile.relation.father': 'Father',
  'profile.relation.grandmother': 'Grandmother',
  'profile.relation.grandfather': 'Grandfather',
  'profile.relation.aunt': 'Aunt',
  'profile.relation.uncle': 'Uncle',
  'profile.relation.sister': 'Sister',
  'profile.relation.brother': 'Brother',
  'profile.relation.cousin': 'Cousin',
  'profile.relation.godmother': 'Godmother',
  'profile.relation.godfather': 'Godfather',
  'profile.relation.nanny': 'Nanny',
  'profile.relation.carer': 'Carer',
  'profile.relation.relative': 'Relative',
  'profile.relation.friend': 'Friend',
  'profile.relation.none': 'Not specified',
  'profile.units': 'Units',
  'profile.weightHeight': 'Weight & height',
  'profile.headCirc': 'Head circumference',
  'profile.alwaysCm': 'always cm',
  'profile.accountTitle': 'Your Account',
  'profile.account': 'Your Account',
  'profile.verified': 'Verified',
  'profile.unverified': 'Unverified (Click to check)',
  'profile.resendEmail': 'Resend Link',
  'profile.changeEmail': 'Change Email',
  'profile.changePassword': 'Change Password',
  'profile.apiKeys': 'API Keys',
  'profile.providerManaged': 'Linked with {provider}. Manage credentials via your provider.',
  'profile.bornWeeks': 'Born {dob} · {weeks} weeks old',
  'profile.editAria': 'Edit profile',
  'profile.bornWks': 'Born {dob} · {weeks} wks',
  'profile.joinBaby': 'Join a baby',
  'profile.mediaGallery': 'Media Gallery',
  'profile.subscriptionRequired': 'Subscription Required',
  'profile.activeSubRequiredTitle': 'Active Subscription Required',
  'profile.activeSubRequiredDesc': 'This is only available with an active subscription.',
  'profile.showPlan': 'Show Plan',
  'profile.sharingLimitReached': 'Sharing Limit Reached',
  'profile.limitReachedTitle': 'Sharing Limit Reached',
  'profile.limitReachedDesc': 'You have reached your limit.',
  'sharing.title': 'Sharing & access',
  'sharing.rowHint': 'Invite a partner or carer to {name}',
  'common.success': 'Success!',
  'profile.accountSettings': 'Account Settings',
  'profile.emailUpdateSuccess': 'Your email has been updated. A verification link has been sent to your new email address.',
  'profile.passwordUpdateSuccess': 'Your password has been successfully updated.',
  'profile.email': 'Email',
  'profile.password': 'Password',
  'profile.changeEmailTitle': 'Update your email',
  'profile.changePasswordTitle': 'Change your password',
  'profile.changeEmailDesc': 'Update the email address you use to sign in and receive notifications.',
  'profile.changePasswordDesc': 'Choose a secure new password to keep your account safe.',
  'profile.currentPassword': 'Current Password',
  'profile.currentPasswordRequired': 'Current password is required to save changes.',
  'profile.newEmail': 'New Email Address',
  'profile.invalidEmail': 'Please enter a valid email address.',
  'profile.newPassword': 'New Password',
  'profile.atLeast6': 'At least 6 characters',
  'profile.invalidPassword': 'Password must be at least 6 characters.',
  'profile.confirmPassword': 'Confirm New Password',
  'profile.passwordsMustMatch': 'Passwords do not match.',
  'profile.updateEmailCTA': 'Update Email',
  'profile.updatePasswordCTA': 'Update Password',

  // Baby Form Sheet
  'profile.form.meetTitle': "Let's meet your little one.",
  'profile.form.updateTitle': 'Update their details.',
  'profile.form.meetDesc': 'Just three things so milestones line up with their first year. You can change any of this later.',
  'profile.form.updateDesc': 'You can change the name, date of birth or theme accent at any time.',
  'profile.form.nameLabel': 'Name',
  'profile.form.namePlaceholder': 'e.g. Lena',
  'profile.form.nameError': "Please enter your baby's name.",
  'profile.form.dobLabel': 'Date of birth',
  'profile.form.dobError': 'Please pick a valid date.',
  'profile.form.themeDesc': 'Picks the colour scheme. You can change it any time.',
  'profile.form.addBaby': 'Add baby',
  'profile.form.saveChanges': 'Save changes',
  'profile.feedback': 'Report bug or suggest feature',

  // Feedback Sheet
  'feedback.successTitle': 'Thank you!',
  'feedback.successDesc': 'Your feedback has been successfully submitted. We appreciate your help in improving BabyNook!',
  'feedback.title': 'Help us improve.',
  'feedback.description': 'Found a bug? Or have an idea for a new feature? Let us know below.',
  'feedback.typeLabel': 'Feedback Type',
  'feedback.typeBug': 'Bug Report',
  'feedback.typeFeature': 'Feature Suggestion',
  'feedback.areaLabel': 'Area of the App',
  'feedback.messageLabel': 'Description',
  'feedback.messagePlaceholderBug': 'What happened? What did you expect to happen?',
  'feedback.messagePlaceholderFeature': 'Describe the feature you would like to see and why.',
  'feedback.messageError': 'Please enter a detailed description.',
  'feedback.contactCheckbox': 'Allow BabyNook team to contact you via email for more details.',
  'feedback.submitError': 'Failed to submit. Please try again.',

  // Today / Dashboard Tab
  'today.greeting.morning': 'Good morning',
  'today.greeting.afternoon': 'Good afternoon',
  'today.greeting.evening': 'Good evening',
  'today.dashboard.whereIs': 'Where {name} is for this age',
  'today.dashboard.title': 'Dashboard',
  'today.dashboard.reachBy': 'milestones babies typically reach by week {weeks}',
  'today.dashboard.onTrack.high': 'Right on track',
  'today.dashboard.onTrack.med': 'Nicely on track',
  'today.dashboard.onTrack.low': 'A few still to come',
  'today.workingOn.title': 'Working on',
  'today.workingOn.milestone': 'milestone',
  'today.workingOn.milestones': 'milestones',
  'today.workingOn.none': 'Nothing flagged right now — rest counts too.',
  'today.upNext.title': 'Up next',
  'today.upNext.meta': '{count} to watch for',
  'today.upNext.typicallyNow': 'typically now',
  'today.upNext.notYet': 'Not yet',
  'today.upNext.iSawThis': 'I saw this!',
  'today.activities.title': 'Activities',
  'today.activities.browseAll': 'Browse all activities',
  'today.idea.title': "Today's idea",
  'today.idea.fresh': "Today's idea · fresh",
  'today.idea.busy': 'Thinking of a fresh idea…',
  'today.idea.fallback': "A tailored 3-minute activity for {name}, based on the milestone they're working on right now.",
  'today.idea.generating': 'Generating…',
  'today.idea.generateBtn': "Generate today's idea",
  'today.idea.error': "Couldn't generate right now — please try again in a moment.",
  'today.idea.openMilestone': 'Open milestone',
  'today.idea.doneToday': 'Done today',
  'today.idea.markDone': 'Mark done',
  'today.smile.title': 'A small smile',
  'today.growth.desc': 'Weight, height & head curves with percentiles',
  'today.comingUp.title': 'Coming up',
  'today.comingUp.weeks': 'Weeks {start} – {end}',
  'today.comingUp.milestone': 'Milestone',
  'today.comingUp.gear': 'Gear',
  'today.recent.title': 'Last 2 weeks',
  'today.recent.reachedSingle': '1 milestone reached',
  'today.recent.reachedPlural': '{count} milestones reached',
  'today.recent.none': 'No milestones reached in the last two weeks — every baby moves at their own pace.',
  'today.recent.moments': 'Recent moments',
  'today.recent.recapSoon': "A written recap of {name}'s fortnight is coming soon.",
  'journey.today.openToday': "Open today's screen",
  'journey.today.trackGrowth': 'Track growth',
  'journey.eyebrow': 'Journey',
  'journey.title': "{name}'s path",
  'journey.subtitle': 'Birth through 24 months',
  'journey.beginning': 'The beginning',
  'journey.birth': 'Birth',
  'journey.weekLabel': 'Week {n}',

  // Timeline view (Milestones)
  'timeline.eyebrow': 'Timeline',
  'timeline.subtitle': 'First year, at a glance',
  'timeline.achievedOn': 'Achieved on {date}',
  'timeline.startedOn': 'Started on {date}',
  'timeline.upcomingDetail': 'Start: wk {start} · Median: wk {median} · Most: wk {bandLo}–{bandHi} · Flag: wk {flag}',

  // Today — Next Steps (actionable task cards)
  'today.title': 'Today',
  'today.headline': '{name} is currently in week {week}',
  'today.tasks.title': 'Next Steps',
  'today.tasks.count': '{count} tasks',
  'tasks.verifyEmail.title': 'Confirm your email',
  'tasks.verifyEmail.desc': 'Please confirm your email address. You have {days} days remaining.',
  'tasks.verifyEmail.expiredDesc': 'Please confirm your email address to keep access to your account.',
  'tasks.verifyEmail.cta': 'Resend Link',
  'tasks.verifyEmail.sentCTA': 'Sent!',
  'tasks.startTrial.title': 'Try Family free for 2 weeks',
  'tasks.startTrial.desc': 'Unlock AI activities, milestones, and unlimited photo storage.',
  'tasks.startTrial.cta': 'Start Free Trial',
  'tasks.shareBaby.title': 'Invite a co-parent',
  'tasks.shareBaby.desc': "Share {name}'s profile with a partner to track milestones together.",
  'tasks.shareBaby.cta': 'Invite Now',
  'tasks.enterGrowth.title': 'Enter growth data',
  'tasks.enterGrowth.emptyDesc': 'Add weight, height, or head size to see growth curves.',
  'tasks.enterGrowth.oldDesc': "It's been a while since your last growth record. Let's check in!",
  'tasks.enterGrowth.cta': 'Add Measurement',

  // Milestones List & Filtering
  'milestones.title': 'Milestones',
  'milestones.subtitle': 'The first 24 months',
  'milestones.view.list': 'List',
  'milestones.view.timeline': 'Timeline',
  'milestones.filter.all': 'All',
  'milestones.filter.open': 'Open',
  'milestones.filter.now': 'On track',
  'milestones.filter.soon': 'Upcoming',
  'milestones.filter.done': 'Achieved',
  'milestones.group.category': 'Category',
  'milestones.group.leap': 'Leaps',
  'milestones.group.date': 'Date',
  'milestones.group.startsLabel': 'By start age',
  'milestones.group.startsSublabel': 'earliest first',
  'milestones.group.peaksWk': 'peaks wk {week}',
  'milestones.group.Motor - Gross': 'Motor – Gross',
  'milestones.group.Motor - Fine': 'Motor – Fine',
  'milestones.group.Communication': 'Communication',
  'milestones.group.Social & Emotional': 'Social & Emotional',
  'milestones.group.Cognitive': 'Cognitive',
  'milestones.group.Boundary Stone': 'Boundary Stone',
  'milestones.group.Language': 'Language',
  'milestones.group.Nutrition': 'Nutrition',
  'milestones.group.Self-Care': 'Self-Care',
  'milestones.group.Sleep': 'Sleep',
  'milestones.group.Teeth': 'Teeth',
  'milestones.todayTask': "Today's task",
  'milestones.recommendedGear': 'Recommended gear',

  // Status Chip / Update Status Sheet
  'status.achieved': 'Achieved',
  'status.inProgress': 'In progress',
  'status.upcoming': 'Upcoming',
  'status.notStarted': 'Not started',
  'status.updateTitle': 'Update status',
  'status.sheetLabel': 'Update milestone status',
  'status.usuallyMonths': 'usually {start}–{end} months',
  'status.selected': 'Selected',
  'status.desc.done': "You've seen this happen — clearly and more than once.",
  'status.desc.now': 'Starting to emerge — some attempts, not yet consistent.',
  'status.desc.soon': "Haven't noticed this yet — most likely just early.",
  'status.when': 'When did it happen?',
  'status.when.today': 'Today',
  'status.when.yesterday': 'Yesterday',
  'status.when.thisweek': 'This week',
  'status.when.custom': 'Pick a date',
  'status.when.customHelp': 'Any day from birth up to today.',
  'status.saveMemory': 'Save a memory · optional',
  'status.tailoredNudge': "We'll surface tailored daily tasks to nudge this one along.",
  'status.saveStatus': 'Save status',

  // Milestone Detail View
  'detail.milestone': 'Milestone',
  'detail.milestoneReached': 'Milestone reached',
  'detail.note': 'Note',
  'detail.trainingIdeaTitle': "Today's training idea",
  'detail.trainingIdeaGenerating': 'Generating idea…',
  'detail.trainingIdeaCreateBtn': 'Generate a new training idea',
  'detail.trainingIdeaHelp': 'Tailored for {name} · 3 minutes · uses what you have',
  'detail.trainingIdeaExplain': 'Explain it',
  'detail.trainingIdeaExplained': 'Explained',
  'detail.trainingIdeaWhy': 'Why it helps',
  'detail.trainingIdeaThinking': 'Thinking…',
  'detail.trainingIdeaRead': 'Read it out',
  'detail.trainingIdeaStop': 'Stop',
  'detail.trainingIdeaMarked': 'Marked done',
  'detail.savedRecord': "Saved to {name}'s record",
  'detail.justNowCounts': 'Just now · counts toward {milestone}',
  'detail.addPhotoOptional': 'Add a photo or video · optional',
  'detail.notLoaded': "Couldn't load this milestone.",
  'detail.back': 'Back',
  'detail.seeDetail': 'See detail',
  'detail.typicalTiming': 'Typical timing',
  'detail.timing.starts': 'Starts',
  'detail.timing.median': 'Median',
  'detail.timing.stddev': 'Std dev',
  'detail.timing.flag': 'Flag',
  'detail.timing.wkavg': 'wk avg',
  'detail.timing.wk': 'wk',
  'detail.aboutThis': 'About this milestone',
  'detail.aboutThisPlay': 'About this play',
  'detail.aboutThisSong': 'About this song',
  'detail.aboutThisBook': 'About this book',
  'detail.aboutThisAdventure': 'About this adventure',
  'detail.aboutThisActivity': 'About this activity',
  'detail.visuals': 'Visuals',
  'detail.recommendedGear': 'Recommended gear',
  'gear.viewOnAmazon': 'View on Amazon',
  'gear.searchOnGoogle': 'Search on Google',
  'gear.opening': 'Opening…',
  'gear.openFailed': "Couldn't open the link.",
  'detail.whatToTry': 'What to try',
  'detail.whatToAvoid': 'What to avoid',
  'detail.buildsOn': 'Builds on',
  'detail.pastActivity': 'Past activity',
  'detail.photos': 'Photos',

  // Milestone Timing Breakdown
  'timing.sheetLabel': 'Milestone timing detail',
  'timing.timingTitle': 'Typical timing',
  'timing.timingDesc': 'When most babies reach this, and where {name} is along the way. Ages are weeks from birth.',
  'timing.legend.most': 'most babies',
  'timing.legend.start': 'start',
  'timing.legend.median': 'median',
  'timing.legend.flag': 'flag',
  'timing.legend.notYet': 'not yet',
  'timing.explain.title': 'What these mean',
  'timing.explain.starts.term': 'Starts',
  'timing.explain.starts.desc': "The average age babies first begin showing early signs of this skill. Before this, it's simply too early — there's nothing to worry about.",
  'timing.explain.median.term': 'Median',
  'timing.explain.median.desc': "The midpoint age: half of babies have reached this milestone by here, half a little later. It's a typical target, not a deadline.",
  'timing.explain.stddev.term': 'Std dev',
  'timing.explain.stddev.desc': 'How spread out the timing is. About two-thirds of babies achieve it within one standard deviation of the median (the shaded band, wk {low}–{high}). Wide bands mean a very normal range of variation.',
  'timing.explain.flag.term': 'Flag',
  'timing.explain.flag.desc': "A gentle check-in point. If {name} hasn't shown this by around here, it can be worth mentioning to your pediatrician — most often it's still perfectly fine, just later.",

  // Activities Tab
  'activities.title': 'Activities',
  'activities.category.favorites': 'Favorites',
  'activities.category.play': 'Play',
  'activities.category.sing': 'Sing',
  'activities.category.music': 'Music',
  'activities.category.read': 'Read',
  'activities.category.excursion': 'Adventure',
  'activities.subtitle': 'Things to do together',
  'activities.suggestedToday': 'Suggested for today',
  'activities.aiPicks': 'AI picks for {name}',
  'activities.suggestBusy': 'Thinking of ideas…',
  'activities.suggestNone': 'No suggestions right now — try “New ideas” below.',
  'activities.suggestReroll': 'New ideas',
  'activities.suggestRerollBusy': 'Finding…',
  'activities.suggestAi': 'AI ideas',
  'activities.suggestAiBusy': 'Generating…',
  'activities.suggestError': "Couldn't generate AI ideas right now — please try again in a moment.",
  'activities.catalog.loading': 'Loading activities…',
  'activities.catalog.none': 'No activities in this category yet.',
  'activities.card.compact': 'All',
  'activities.state.done': 'Done',
  'activities.state.saved': 'Saved',
  'activities.state.notInterested': 'Not interested',
  'activities.state.new': 'New',
  'activities.filter.age': 'Age range',
  'activities.filter.status': 'Status',
  'activities.age.all': 'All ages',
  'activities.age.younger': 'Younger babies',
  'activities.age.older': 'Older babies',
  'activities.status.new': 'New',
  'activities.status.saved': 'Saved',
  'activities.status.done': 'Completed',
  'activities.status.notInterested': 'Not for us',
  'activities.status.all': 'All',

  // Activity Detail View
  'activities.detail.label': 'Activity',
  'activities.detail.notFound': 'Activity not found.',
  'activities.detail.loading': 'Loading…',
  'activities.detail.bestAround': 'Best around {from}–{to} weeks',
  'activities.detail.typicalTiming': 'Typical Timing',
  'activities.detail.bestAge': 'Best Age',
  'activities.detail.babyAge': 'Baby Age',
  'activities.detail.allAges': 'All ages',
  'activities.detail.photos': 'Photos',
  'activities.detail.howTo': 'How to do it',
  'activities.detail.lyrics': 'Lyrics',
  'activities.detail.yourPhotos': 'Your photos',
  'activities.detail.addPhoto': 'Add photo',
  'activities.detail.addPhotoShort': 'Add',
  'activities.detail.uploadFailed': 'Upload failed.',
  'activities.detail.action.notForUs': 'Not for us',
  'activities.detail.action.saveLater': 'Save for later',
  'activities.detail.action.weDidThis': 'We did this!',
  'activities.detail.action.doneCheck': 'Done ✓',

  // Developmental Leaps Tab
  'leaps.title': 'Developmental Leaps',
  'leaps.gatewayTitle': 'Ten gateways\nthrough the first\n20 months',
  'leaps.gatewayDesc': 'Each leap is a window of cognitive change — and the door from which a cluster of milestones tends to emerge.',
  'leaps.section.all': 'All ten leaps',
  'leaps.callout.title': 'Current leap · {deltaLabel}',
  'leaps.callout.delta.now': 'You are here now',
  'leaps.callout.delta.past': '{count} wks past peak',
  'leaps.callout.delta.future': '{count} wks until peak',
  'leaps.callout.reached': '{done}/{total} milestones reached · peaks at wk {week}',
  'leaps.callout.open': 'Open',
  'leaps.card.past': 'Behind us',
  'leaps.card.current': 'Now',
  'leaps.card.upcoming': 'Upcoming',
  'leaps.card.peaks': 'Peaks wk {week}',
  'leaps.card.reached': '{done}/{total} reached',
  'leaps.card.none': 'No milestones yet',

  // Leap Detail Sheet
  'leaps.sheet.label': 'Leap detail',
  'leaps.sheet.title': 'Leap {id} · peaks wk {week}',
  'leaps.sheet.milestonesTitle': 'Milestones in this leap',
  'leaps.sheet.none': 'No milestones in this leap yet.',

  // Physical Growth Tab
  'growth.title': 'Growth',
  'growth.subtitle': 'How {name} is growing',
  'growth.desc': 'We plot weight, height and head circumference against WHO percentiles. The goal band is P25–P75.',
  'growth.metric.weight': 'Weight',
  'growth.metric.height': 'Height',
  'growth.metric.head': 'Head',
  'growth.deleteConfirm': 'Delete this measurement?',
  'growth.summary.latest': 'Latest · wk {week}',
  'growth.summary.recorded': 'Recorded {date}',
  'growth.table.title': 'All measurements',
  'growth.table.entry': '1 entry',
  'growth.table.entries': '{count} entries',
  'growth.table.loading': 'Loading measurements…',
  'growth.table.empty': "No measurements yet. Add {name}'s first weigh-in to start the chart.",
  'growth.table.age': 'Age',
  'growth.table.weight': 'Weight',
  'growth.table.height': 'Height',
  'growth.table.head': 'Head',
  'growth.table.wk': 'wk {week}',
  'growth.cta.add': 'Add measurement',
  'growth.state.inTarget': 'In target',
  'growth.state.belowTarget': 'P3–P25',
  'growth.state.aboveTarget': 'P75–P97',
  'growth.state.low': 'Below P3',
  'growth.state.high': 'Above P97',
  'growth.chart.title.weight': 'Weight-for-age',
  'growth.chart.title.height': 'Length-for-age',
  'growth.chart.title.head': 'Head circumference',
  'growth.chart.who.boy': 'WHO boys 0–20 mo',
  'growth.chart.who.girl': 'WHO girls 0–20 mo',
  'growth.chart.who.neutral': 'WHO 0–20 mo',
  'growth.chart.weekTick': '{w}w',
  'growth.chart.legend.goal': 'Goal · P25–P75',
  'growth.chart.legend.p50': 'P50 median',
  'growth.chart.legend.p397': 'P3 / P97',

  // Settings sheet
  'settings.title': 'Settings',
  'settings.categories.title': 'Milestone Categories',
  'settings.categories.desc': 'Choose which development categories you want to track. Deselected categories will be hidden from the UI, statistics, leaps, and lists.',
  'settings.legal.title': 'Legal',
  'settings.legal.terms': 'Terms and Conditions',
  'settings.legal.privacy': 'Privacy Policy',
  'settings.legal.accepted': 'Accepted {version}',
  'settings.legal.notAccepted': 'Not yet accepted',

  // Growth Add Sheet
  'growth.add.sheetLabel': 'Add measurement',
  'growth.add.title': 'How is {name} today?',
  'growth.add.desc': 'Fill at least one — leave the others blank.',
  'growth.add.when': 'When',
  'growth.add.when.today': 'Today',
  'growth.add.when.yesterday': 'Yesterday',
  'growth.add.when.custom': 'Pick a date',
  'growth.add.when.customHelp': 'Any day from birth up to today.',
  'growth.add.optional': '· optional',
  'growth.add.errorEmpty': 'Please fill at least one of weight, height or head circumference.',
  'growth.add.errorSave': 'Could not save measurement. Please try again.',
  'growth.add.save': 'Save measurement',

  // Comments & reactions (engagement)
  'common.loading': 'Loading…',
  'engagement.title': 'Comments & reactions',
  'engagement.commentsShort': 'Comments',
  'engagement.addReaction': 'Add reaction',
  'engagement.moreEmoji': 'More emoji',
  'engagement.commentPlaceholder': 'Write a comment…',
  'engagement.post': 'Post',
  'engagement.bold': 'Bold',
  'engagement.italic': 'Italic',
  'engagement.delete': 'Delete',
  'engagement.someone': 'Someone',
  'engagement.justNow': 'just now',

  // Questionnaires (date-triggered reflection forms)
  'questionnaire.eyebrow': 'A moment to remember',
  'questionnaire.desc': 'Capture this moment — it will appear on the Journey.',
  'questionnaire.ownWords': 'or in your own words…',
  'questionnaire.save': 'Save',
  'questionnaire.saveDraft': 'Save draft',
  'questionnaire.errorSave': 'Could not save. Please try again.',
  'questionnaire.fillOut': 'Fill out',
  'questionnaire.edit': 'Edit',
  'questionnaire.completed': 'Saved',
  'questionnaire.nextSteps.title': 'Capture a memory',
  'questionnaire.nextSteps.desc': 'Take a moment to reflect on {title}.',
  'questionnaire.nextSteps.cta': 'Start',
  'questionnaire.photos': 'Photos & videos',
  'questionnaire.addPhoto': 'Add photo',
  'questionnaire.addPhotoShort': 'Add',
  'questionnaire.uploadFailed': 'Upload failed.',

  // Notification center
  'notifications.title': 'Notifications',
  'notifications.latestFor': 'Latest for {name}',
  'notifications.someone': 'Someone',
  'notifications.empty': 'Nothing here yet. Photos, milestones and other updates from everyone caring for {name} will show up here.',
  'notifications.photoAdded': '{actor} added a new photo of {baby}',
  'notifications.photosAdded': '{actor} added {count} new photos of {baby}',
  'notifications.milestoneAchieved': '{actor} marked a milestone as achieved',
  'notifications.milestoneAchievedTitled': '{actor} marked \u201C{title}\u201D as achieved',
  'notifications.milestonesAchieved': '{actor} marked {count} milestones as achieved',
  'notifications.growthAdded': '{actor} added a growth measurement for {baby}',
  'notifications.commentAdded': '{actor} left a comment',
  'notifications.activityCompleted': '{actor} completed an activity with {baby}',
  'notifications.activityCompletedTitled': '{actor} completed \u201C{title}\u201D with {baby}',
  'notifications.questionnaireAnswered': '{actor} answered a questionnaire about {baby}',
  'notifications.memberJoined': '{actor} joined {baby}\u2019s care team',
  'notifications.genericEvent': 'New activity for {baby}',
  'notifications.pushDenied': 'Browser notifications are blocked for this site \u2014 updates will still appear in the app.',
  'notifications.settings.title': 'Notifications',
  'notifications.settings.desc': 'Get notified when others add photos, milestones and more. Choose per baby whether you want every update right away or a daily/weekly summary.',
  'notifications.settings.allow': 'Allow notifications',
  'notifications.settings.frequency': 'Frequency',
  'notifications.freq.immediate': 'Immediate',
  'notifications.freq.daily': 'Daily',
  'notifications.freq.weekly': 'Weekly',
  'common.locked': 'Locked',
  'media.loadFailed': 'Couldn\'t load this media.',

  // Onboarding — baby step (create / join)
  'onboarding.baby.eyebrow': 'Your baby',
  'onboarding.baby.titleCreate': 'Let’s meet\nyour little one.',
  'onboarding.baby.titleJoin': 'Join the\nlittle one.',
  'onboarding.baby.subtitleCreate': 'Just three things so milestones line up with their first year. You can change any of this later.',
  'onboarding.baby.subtitleJoin': 'Enter the invite link or code a parent shared with you.',
  'onboarding.baby.toggleCreate': 'Create new',
  'onboarding.baby.toggleJoin': 'Join existing',
  'onboarding.baby.joinLabel': 'Invite link or code',
  'onboarding.baby.joinPlaceholder': 'Paste link or code',
  'onboarding.baby.checking': 'Checking…',
  'onboarding.baby.inviteInvalid': 'This invite is invalid or has expired.',
  'onboarding.baby.youllJoin': 'You’ll join {name}',
  'onboarding.baby.joinNextHint': 'Create or sign in to your account next, then you’re in.',
  'onboarding.baby.nameLabel': 'Name',
  'onboarding.baby.namePlaceholder': 'e.g. Lena',
  'onboarding.baby.nameRequired': 'Please enter your baby’s name.',
  'onboarding.baby.dobLabel': 'Date of birth',
  'onboarding.baby.dobInvalid': 'Please pick a valid date.',
  'onboarding.baby.themeLabel': 'Theme accent',
  'onboarding.baby.themeDesc': 'Picks the colour scheme. You can change it any time.',
  'onboarding.continue': 'Continue',

  // Onboarding — milestone preview step
  'onboarding.milestonePreview.markSeen': 'Seen together',
  'onboarding.milestonePreview.markAchieved': 'Achieved',

  // Onboarding — milestone catch-up step
  'onboarding.catchup.skipButton': 'Skip catch-up',
  'onboarding.catchup.eyebrow': 'Onboarding Customisation',
  'onboarding.catchup.title': 'Catch up on milestones',
  'onboarding.catchup.explainer': 'Since {name} is already {weeks} weeks old, many developmental milestones may have already occurred. Select your starting week range to quickly log what has happened so far, saving you from marking them one-by-one later.',
  'onboarding.catchup.upTo': 'Milestones up to:',
  'onboarding.catchup.week': 'Week {week}',
  'onboarding.catchup.weekBirth': 'Week 0 (Birth)',
  'onboarding.catchup.weekMax': 'Week {week} (Max Milestone: {max})',
  'onboarding.catchup.section': 'Developmental milestones ({count})',
  'onboarding.catchup.emptyRange': 'Slide the selector above to load milestones by week range.',
  'onboarding.catchup.selected': '{count} milestones selected',
  'onboarding.catchup.remainingNote': 'Remaining milestones will default to not started.',
  'onboarding.catchup.save': 'Save & Continue',
  'onboarding.catchup.saving': 'Saving details…',
  'onboarding.catchup.achievedOn': 'Achieved on:',

  // Onboarding — auth step (signup / signin / forgot)
  'onboarding.auth.eyebrowSignup': 'Create your account',
  'onboarding.auth.eyebrowSignin': 'Welcome back',
  'onboarding.auth.eyebrowForgot': 'Reset password',
  'onboarding.auth.titleSignup': 'One last step,\nthen it’s all {name}.',
  'onboarding.auth.titleSignin': 'Good to see you.\nPick up where you left off.',
  'onboarding.auth.titleForgot': 'Reset your\npassword.',
  'onboarding.auth.subSignup': 'We’ll save your milestones across devices and let you invite a co-parent later.',
  'onboarding.auth.subSignin': 'Sign in to sync this device with your existing account.',
  'onboarding.auth.subForgot': 'Enter your email address and we’ll send you a link to reset your password.',
  'onboarding.auth.toggleCreate': 'Create account',
  'onboarding.auth.toggleSignin': 'I have an account',
  'onboarding.auth.emailLabel': 'Email',
  'onboarding.auth.passwordLabel': 'Password',
  'onboarding.auth.emailPlaceholder': 'you@example.com',
  'onboarding.auth.passwordPlaceholderSignup': 'At least 6 characters',
  'onboarding.auth.passwordHint': '{n} more character{suffix} to go.',
  'onboarding.auth.termsPrefix': 'I agree to the',
  'onboarding.auth.termsConditions': 'Terms & Conditions',
  'onboarding.auth.termsAnd': 'and',
  'onboarding.auth.termsPrivacy': 'Privacy Policy',
  'onboarding.auth.resetMockSent': 'Mock password reset email sent! (Success simulated in mock mode).',
  'onboarding.auth.resetSent': 'A password reset link has been sent to your email.',
  'onboarding.auth.or': 'or',
  'onboarding.auth.google': 'Continue with Google',
  'onboarding.auth.forgotLink': 'Forgot password?',
  'onboarding.auth.backToSignin': 'Back to sign in',
  'onboarding.auth.loadingSignup': 'Creating account…',
  'onboarding.auth.loadingSignin': 'Signing in…',
  'onboarding.auth.loadingForgot': 'Sending link…',
  'onboarding.auth.submitSignin': 'Sign in',
  'onboarding.auth.submitForgot': 'Send reset link',
  'onboarding.auth.hintHaveAccount': 'Already have an account?',
  'onboarding.auth.hintNewHere': 'New here?',
  'onboarding.auth.hintSignupLink': 'Create an account',

  // Onboarding — metrics step (DOB, theme, why-it-matters fact)
  'onboarding.metrics.title': 'A few details\nabout your little one.',
  'onboarding.metrics.genderTheme': 'Gender & theme',
  'onboarding.metrics.themeDesc': 'Sets the colour accent. Change it any time.',
  'onboarding.metrics.whyItMatters': 'Why it matters',
  'onboarding.metrics.factTitle': 'The brain architecture',
  'onboarding.metrics.factQuote': 'Every single second, your baby’s brain builds 1,000 new neural pathways—shaping a lifetime of learning in just 24 months.',

  // Dashboard (progress drill-in from Today)
  'dashboard.category.motorSkills': 'Motor Skills',
  'dashboard.category.boundaryStones': 'Boundary Stones',
  'dashboard.activity.play': 'Play',
  'dashboard.activity.sing': 'Sing',
  'dashboard.activity.music': 'Music',
  'dashboard.activity.read': 'Read',
  'dashboard.activity.excursion': 'Excursion',
  'dashboard.perf.ahead': 'Ahead',
  'dashboard.perf.median': 'In the median',
  'dashboard.perf.late': 'Late',
  'dashboard.perf.atRisk': 'At risk',
  'dashboard.title': 'Dashboard',
  'dashboard.heading': '{name}’s progress\nso far',
  'dashboard.summary': '{done} milestones reached out of {total} tracked across the first 24 months.',
  'dashboard.stat.onTrack': 'On track',
  'dashboard.stat.upcoming': 'Upcoming',
  'dashboard.weekOf': 'Week of ',
  'dashboard.action': 'action',
  'dashboard.actions': 'actions',
  'dashboard.quietWeek': 'A quiet week. Rest counts too.',
  'dashboard.quietDay': 'A quiet day. Rest counts too.',
  'dashboard.milestonesWon': 'Milestones won',
  'dashboard.milestonesStarted': 'Milestones started',
  'dashboard.activitiesDone': 'Activities done',
  'dashboard.byCategory': 'By category',
  'dashboard.total': 'total',
  'dashboard.images': 'Images',
  'dashboard.photoCaptured': 'photo captured',
  'dashboard.photosCaptured': 'photos captured',

  // Photo Encryption (E2EE setup / unlock sheet & settings card)
  'encryption.error.tooShort': 'Use at least {min} characters.',
  'encryption.error.mismatch': 'The two passwords don’t match.',
  'encryption.error.notConfirmed': 'Please confirm you’ve written the password down.',
  'encryption.error.saveChoice': 'Could not save your choice.',
  'encryption.error.enable': 'Could not enable encryption.',
  'encryption.error.wrongPassword': "That password doesn't match. Please try again.",
  'encryption.sheet.labelUnlock': 'Unlock your photos',
  'encryption.sheet.labelSetup': 'Protect your photos',
  'encryption.unlock.kicker': 'Encrypted',
  'encryption.unlock.title': 'Enter your photo password',
  'encryption.unlock.sub': 'Your photos on this account are end-to-end encrypted. Enter the password you set to view and add photos on this device.',
  'encryption.unlock.busy': 'Unlocking…',
  'encryption.unlock.cta': 'Unlock',
  'encryption.hintPrefix': 'Hint:',
  'encryption.field.password': 'Password',
  'encryption.field.confirmPassword': 'Confirm password',
  'encryption.field.hintLabel': 'Hint',
  'encryption.field.hintOptional': '· optional, stored unencrypted',
  'encryption.field.hintPlaceholder': 'e.g. our usual passphrase',
  'encryption.choice.kicker': 'Your photos, your choice',
  'encryption.choice.title': 'Keep these photos private?',
  'encryption.choice.sub': "You're about to upload a photo. Choose how it's stored — you can't change a photo's protection after it's uploaded.",
  'encryption.choice.e2eeTitle': 'End-to-end encrypted',
  'encryption.choice.bullet1': '· Photos are scrambled on your device before upload — only you can open them.',
  'encryption.choice.bullet2': '· Not even we (or anyone with server access) can see them.',
  'encryption.choice.bullet3': '· Works across your phone and other devices once you enter your password.',
  'encryption.choice.importantLabel': 'Important:',
  'encryption.choice.importantText': "we never see your password, so we can't reset it. If you forget it, these photos are lost permanently. Write it down somewhere safe.",
  'encryption.choice.setPasswordCta': 'Set a password & encrypt',
  'encryption.choice.noneTitle': 'Store without encryption',
  'encryption.choice.noneDesc': 'Simpler — no password to remember. Photos are stored normally on the server. Choose this if convenience matters more than maximum privacy.',
  'encryption.choice.noneCta': 'Continue without encryption',
  'encryption.choice.notNow': 'Not now',
  'encryption.setup.kicker': 'End-to-end encryption',
  'encryption.setup.title': 'Choose your photo password',
  'encryption.setup.sub': 'This password encrypts your photos. We never receive it — keep it safe.',
  'encryption.setup.confirmPrefix': 'I understand that if I lose this password,',
  'encryption.setup.confirmBold': "my encrypted photos can't be recovered",
  'encryption.setup.confirmSuffix': "I've written it down somewhere safe.",
  'encryption.setup.busy': 'Encrypting…',
  'encryption.setup.cta': 'Enable encryption',
  'encryption.settings.title': 'Photo privacy',
  'encryption.settings.rowLabel': 'End-to-end encryption',
  'encryption.settings.notSet': 'Not set',
  'encryption.settings.passwordLabel': 'Your photo password',
  'encryption.settings.keepSafeWarning': "Keep this password somewhere safe. We can't reset it — if it's lost, your encrypted photos can't be recovered.",
  'encryption.settings.unlockCta': 'Enter password to unlock on this device',
  'encryption.settings.noneNote': 'Photos are stored without encryption. Your choice applies to new uploads.',

  // Photo/video upload sheet
  'upload.header': 'Add photos & videos',
  'upload.selectedMedia': 'Selected media ({n}/12)',
  'upload.add': 'Add',
  'upload.remove': 'Remove',
  'upload.typeOfMoments': 'Type of moments',
  'upload.generalMoment': 'General Moment',
  'upload.milestoneAchieved': 'Milestone Achieved',
  'upload.titleOptional': 'Title (optional)',
  'upload.titlePlaceholder': 'e.g. Fun day at the park',
  'upload.descriptionOptional': 'Description (optional)',
  'upload.descriptionPlaceholder': 'Write something about this moment…',
  'upload.selectMilestone': 'Select milestone',
  'upload.chooseMilestonePlaceholder': '-- Choose a milestone --',
  'upload.whenTaken': 'When was it taken?',
  'upload.encryptedNotice': 'Will be end-to-end encrypted',
  'upload.unencryptedNotice': 'Stored without encryption',
  'upload.progressPlain': 'Uploading {done} of {total}…',
  'upload.progressEncrypting': 'Uploading {done} of {total} · encrypting on your device…',
  'upload.cta': 'Upload moments',
  'upload.milestoneRequired': 'Please select a milestone.',
  'upload.videoTooLarge': 'Video "{name}" exceeds the 50MB limit.',
  'upload.imageTooLarge': 'Photo "{name}" exceeds the 30MB limit.',
  'upload.failed': 'Upload failed. Please try again.',

  // Account Deletion
  'account.deletion.title': 'Delete Account',
  'account.deletion.eyebrow': 'Account deletion scheduled',
  'account.deletion.headline': "We're sorry to see you go",
  'account.deletion.description': "Your account is scheduled for deletion. You can cancel any time before it's removed.",
  'account.deletion.descriptionWithDate': 'Your account and its data will be permanently deleted on {date}. You can cancel any time before then.',
  'account.deletion.keepAccountCTA': 'Keep my account',
  'account.deletion.cancelError': 'Could not cancel deletion. Please try again.',
  'account.deletion.requestError': 'Could not start deletion. Please try again.',
  'account.deletion.introTitle': 'This will remove your data',
  'account.deletion.introDesc': 'Deleting your account removes your profile, babies you solely own, and everything in them — milestones, growth records, photos, and comments. You’ll have 30 days to change your mind.',
  'account.deletion.confirmTitle': 'Are you sure?',
  'account.deletion.confirmDesc': 'This starts a 30-day countdown. You can cancel any time before then by signing back in.',
  'account.deletion.startCTA': 'Delete My Account',
  'account.deletion.confirmCTA': 'Yes, delete my account',
  'account.deletion.blockedTitle': 'Remove other members first',
  'account.deletion.blockedDesc': "You're the owner of these babies and other people still have access. Remove them from Sharing, then come back here.",
  'account.deletion.oneOtherMember': '1 other member',
  'account.deletion.otherMembers': '{n} other members',
} as const;

export type DictKey = keyof typeof en;
export type Dictionary = Partial<Record<DictKey, string>> & Record<string, string>;
